import { useState, useEffect, useMemo } from 'react'; // <-- 1. Import useMemo

import { SelectOption, Account } from '~/shared/utils/types';
import { usePanelBehavior } from '~/shared/utils/usePanelBehavior';
import FilterPanelHeader from '~/shared/components/filters/FilterPanelHeader';

import FilterPanelContent from './FilterPanelContent';

export type GroupFleetManagerSelectionOption = {
  label: string;
  options: SelectOption[];
};

interface AutoscaleRulesFiltersSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResetFilters?: () => void;
  appliedRuleIDFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  onApplyFilters?: (filters: {
    active?: boolean[];
    ruleIds?: string[];
    accountIds?: string[];
  }) => void;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
}

export default function AutoscaleRulesFiltersSidePanel({
  open,
  onOpenChange,
  onResetFilters,
  appliedRuleIDFilters,
  appliedAccountFilters,
  appliedAutoscaleState,
  onApplyFilters,
  isOrgView = false,
  accounts = [],
  activeOrgId,
}: AutoscaleRulesFiltersSidePanelProps) {
  const [showActiveRulesOnly, setShowActiveRulesOnly] = useState(false);
  const [pendingRuleIDFilters, setPendingRuleIDFilters] = useState<SelectOption[]>([]);
  const [pendingAccountFilters, setPendingAccountFilters] = useState<SelectOption[]>([]);

  const handleClose = () => {
    // Apply filters when panel closes
    handleApply();
  };

  const { panelRef } = usePanelBehavior(open, onOpenChange, true, undefined, handleClose);

  // Sync pending state with applied filters when panel opens
  useEffect(() => {
    if (open) {
      setPendingRuleIDFilters(appliedRuleIDFilters);
      setPendingAccountFilters(appliedAccountFilters);
      // Set toggle based on applied state
      setShowActiveRulesOnly(
        appliedAutoscaleState.length === 1 && appliedAutoscaleState[0] === 'active'
      );
    }
  }, [open, appliedRuleIDFilters, appliedAccountFilters, appliedAutoscaleState]);

  const filterableAccounts = useMemo(() => {
    if (!activeOrgId) {
      return accounts;
    }
    return accounts.filter((account) => account.accountId !== activeOrgId);
  }, [accounts, activeOrgId]);

  const handleApply = () => {
    const filters: { active?: boolean[]; ruleIds?: string[]; accountIds?: string[] } = {};

    // Only add active filter if toggle is on
    if (showActiveRulesOnly) {
      filters.active = [true];
    }

    // Add rule IDs if any are selected
    if (pendingRuleIDFilters.length > 0) {
      filters.ruleIds = pendingRuleIDFilters.map((option) => option.id);
    }

    // Add account IDs if any are selected
    if (pendingAccountFilters.length > 0) {
      filters.accountIds = pendingAccountFilters.map((option) => option.id);
    }

    onApplyFilters?.(filters);
  };

  const handleReset = () => {
    setShowActiveRulesOnly(false);
    setPendingRuleIDFilters([]);
    setPendingAccountFilters([]);
    onResetFilters?.();
    onOpenChange(false);
  };

  const hasActiveFilters =
    // appliedRuleIDFilters.length > 0 ||
    // appliedAutoscaleState.length > 0 ||
    showActiveRulesOnly || pendingRuleIDFilters.length > 0 || pendingAccountFilters.length > 0;

  return (
    <div
      ref={panelRef}
      className={`fixed -mt[0.125px] right-0 z-[120] w-[587px] bg-white border-l border-primary-70 transform transition-transform duration-300 ease-in-out ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{
        top: 0,
        bottom: 0,
        boxShadow: '-15px 86px 57.4px -3px rgba(0, 0, 0, 0.09)',
      }}
    >
      <div className="flex flex-col h-full">
        <FilterPanelHeader
          onClearAll={handleReset}
          hasActiveFilters={hasActiveFilters}
          onClose={() => {
            handleClose();
            onOpenChange(false);
          }}
        />
        <FilterPanelContent
          panelOpen={open}
          pendingAutoscaleState={appliedAutoscaleState}
          setPendingAutoscaleState={() => {}} // Not used based on commented out props
          pendingRuleIDFilters={pendingRuleIDFilters}
          setPendingRuleIDFilters={setPendingRuleIDFilters}
          pendingAccountFilters={pendingAccountFilters}
          setPendingAccountFilters={setPendingAccountFilters}
          showActiveRulesOnly={showActiveRulesOnly}
          setShowActiveRulesOnly={setShowActiveRulesOnly}
          isOrgView={isOrgView}
          accounts={filterableAccounts}
        />
      </div>
    </div>
  );
}
